import { Toast } from "@compass/core";
import { createSlice } from "@reduxjs/toolkit";
import { LkReactMenu, RolePermission } from "generated/openApi";

interface AppState {
  // general
  toast?: Toast;
  loading: number;
  authStatus?: "AUTHENTICATED" | "NOT_AUTHENTICATED";
  navDrawerOpen?: boolean;

  // requires authentication
  tenantName?: string;
  userMembership?: string;
  centralUserMembership?: string;
  userId?: number;
  roleId?: number;
  userFullName?: string;
  rolePermissions?: RolePermission[];
  reactMenus?: LkReactMenu[];
  tenantTimezone?: string;
  theme?: "Dark" | "Light";
  avatarColor?: string;
}

const initialState: AppState = {
  toast: undefined,
  loading: 0,
  authStatus: undefined,
  navDrawerOpen: false,

  tenantName: undefined,
  userId: undefined,
  userMembership: undefined,
  centralUserMembership: undefined,
  roleId: undefined,
  userFullName: undefined,
  rolePermissions: undefined,
  reactMenus: undefined,
  tenantTimezone: undefined,
  theme: "Light",
  avatarColor: undefined,
};

export const appSlice = createSlice({
  name: "app",
  initialState,
  reducers: {
    setAppToast: (state, action: { type: string; payload: typeof initialState.toast }) => {
      state.toast = action.payload;
    },
    setAppLoading: (state, action: { type: string; payload: typeof initialState.loading }) => {
      state.loading = action.payload;
    },
    setAppAuthStatus: (state, action: { type: string; payload: typeof initialState.authStatus }) => {
      state.authStatus = action.payload;
    },
    setAppNavDrawerOpen: (state, action: { type: string; payload: typeof initialState.navDrawerOpen }) => {
      state.navDrawerOpen = action.payload;
    },
    setAppTenantName: (state, action: { type: string; payload: typeof initialState.tenantName }) => {
      state.tenantName = action.payload;
    },
    setAppUserId: (state, action: { type: string; payload: typeof initialState.userId }) => {
      state.userId = action.payload;
    },
    setAppUserMembership: (state, action: { type: string; payload: typeof initialState.userMembership }) => {
      state.userMembership = action.payload;
    },
    setAppCentralUserMembership: (state, action: { type: string; payload: typeof initialState.centralUserMembership }) => {
      state.centralUserMembership = action.payload;
    },
    setAppRoleId: (state, action: { type: string; payload: typeof initialState.roleId }) => {
      state.roleId = action.payload;
    },
    setAppUserFullName: (state, action: { type: string; payload: typeof initialState.userFullName }) => {
      state.userFullName = action.payload;
    },
    setAppRolePermissions: (state, action: { type: string; payload: typeof initialState.rolePermissions }) => {
      state.rolePermissions = action.payload;
    },
    setAppReactMenus: (state, action: { type: string; payload: typeof initialState.reactMenus }) => {
      state.reactMenus = action.payload;
    },
    setAppTenantTimezone: (state, action: { type: string; payload: typeof initialState.tenantTimezone }) => {
      state.tenantTimezone = action.payload;
    },
    setAppTheme: (state, action: { type: string; payload: typeof initialState.theme }) => {
      state.theme = action.payload;
    },
    setAvatarColor: (state, action) => {
      state.avatarColor = action.payload;
    },
  },
});

// Action creators are generated for each case reducer function
export const {
  setAppToast,
  setAppLoading,
  setAppAuthStatus,
  setAppNavDrawerOpen,
  setAppUserId,
  setAppRoleId,
  setAppUserFullName,
  setAppUserMembership,
  setAppCentralUserMembership,
  setAppTenantName,
  setAppRolePermissions,
  setAppReactMenus,
  setAppTenantTimezone,
  setAppTheme,
  setAvatarColor,
} = appSlice.actions;

export default appSlice.reducer;